// src/context/CartContext.js
// ============================================================================
// CART CONTEXT
//
// Frontend-only shopping cart for merch. Persists to localStorage so the
// cart survives page reloads and the user's session. No backend sync — cart
// state is intentionally device-local until checkout, when it converts into
// a multi-item server order (see backend createMerchCheckoutSession /
// createMerchWalletOrder, both refactored to accept items[]).
//
// API:
//   const { items, itemCount, subtotalCents, addItem, removeItem, setQty, clear }
//     = useCart();
//
// addItem(item) — pass the full line item shape:
//   { productKey, size, sizeLabel, productName, image, unitAmountCents, quantity? }
// Items are keyed by `${productKey}_${size}`; re-adding the same SKU bumps
// quantity rather than creating a duplicate line. Hard cap of 20 lines per
// cart to prevent abuse (any individual line still caps qty at 10, same as
// the backend's per-call cap).
//
// Native fallback: in-memory state only (no AsyncStorage dependency to keep
// the web build lean — the merch flow is web-first).
// ============================================================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';

const STORAGE_KEY = 'critcash:cart:v1';
const MAX_LINES = 20;
const MAX_QTY_PER_LINE = 10;

const CartContext = createContext(null);

const lineKey = (productKey, size) => `${productKey}_${size}`;

// Best-effort localStorage access — wrapped because SSR / Safari private mode
// can throw on access. Failures degrade to in-memory-only state, never crash.
const readStorage = () => {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn('[Cart] localStorage read failed (non-fatal):', err.message);
    return [];
  }
};

const writeStorage = (items) => {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    console.warn('[Cart] localStorage write failed (non-fatal):', err.message);
  }
};

export function CartProvider({ children }) {
  // Hydrate from storage on first render so a page reload restores the cart
  // before any consumer reads from it.
  const [items, setItems] = useState(readStorage);

  // Write-through to storage on every mutation. useEffect (not inside the
  // setter) so React's strict-mode double-invocation doesn't double-write.
  useEffect(() => {
    writeStorage(items);
  }, [items]);

  const addItem = useCallback((line) => {
    if (!line?.productKey || !line?.size) {
      console.warn('[Cart] addItem: productKey and size required', line);
      return;
    }
    const key = lineKey(line.productKey, line.size);
    const addQty = Math.max(1, Math.floor(Number(line.quantity) || 1));

    setItems((prev) => {
      const existingIdx = prev.findIndex((l) => lineKey(l.productKey, l.size) === key);
      if (existingIdx >= 0) {
        // Bump quantity on existing line, capped.
        const next = prev.slice();
        const newQty = Math.min(MAX_QTY_PER_LINE, next[existingIdx].quantity + addQty);
        next[existingIdx] = { ...next[existingIdx], quantity: newQty };
        return next;
      }
      if (prev.length >= MAX_LINES) {
        console.warn(`[Cart] line cap reached (${MAX_LINES}); not adding`, line);
        return prev;
      }
      return [
        ...prev,
        {
          productKey: line.productKey,
          size: line.size,
          sizeLabel: line.sizeLabel || line.size,
          productName: line.productName || line.productKey,
          // image is optional — used by CartScreen for thumbnails. Pass
          // require()'d asset modules from the caller (works in RN Web).
          image: line.image || null,
          unitAmountCents: Math.max(0, Math.floor(Number(line.unitAmountCents) || 0)),
          quantity: Math.min(MAX_QTY_PER_LINE, addQty),
        },
      ];
    });
  }, []);

  const removeItem = useCallback((productKey, size) => {
    const key = lineKey(productKey, size);
    setItems((prev) => prev.filter((l) => lineKey(l.productKey, l.size) !== key));
  }, []);

  const setQty = useCallback((productKey, size, quantity) => {
    const key = lineKey(productKey, size);
    const q = Math.max(0, Math.min(MAX_QTY_PER_LINE, Math.floor(Number(quantity) || 0)));
    setItems((prev) => {
      if (q === 0) return prev.filter((l) => lineKey(l.productKey, l.size) !== key);
      return prev.map((l) =>
        lineKey(l.productKey, l.size) === key ? { ...l, quantity: q } : l
      );
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const itemCount = useMemo(
    () => items.reduce((sum, l) => sum + (l.quantity || 0), 0),
    [items]
  );

  const subtotalCents = useMemo(
    () => items.reduce((sum, l) => sum + (l.unitAmountCents || 0) * (l.quantity || 0), 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, itemCount, subtotalCents, addItem, removeItem, setQty, clear }),
    [items, itemCount, subtotalCents, addItem, removeItem, setQty, clear]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    // Defensive: if a screen mounts before the provider tree is ready (or
    // outside it altogether) return a no-op stub so the screen renders
    // empty instead of crashing.
    console.warn('[Cart] useCart() called outside CartProvider — returning stub');
    return {
      items: [],
      itemCount: 0,
      subtotalCents: 0,
      addItem: () => {},
      removeItem: () => {},
      setQty: () => {},
      clear: () => {},
    };
  }
  return ctx;
}
